<script setup lang="ts">
import { api } from '@/api';
import { AppPermissionsEnum, type UnitDto } from '@/api/generator';
import PtwAddButton from '@/components/buttons/PtwAddButton.vue';
import { getDefaultDeleteConfirmationOptions } from '@/components/confirmationModals/defaults';
import { EntityInfo } from '@/components/confirmationModals/types';
import { useActionWithConfirmation } from '@/components/confirmationModals/useActionWithConfirmation';
import { TAB_PREFIX } from '@/components/confirmationModals/useFormConfirmationModal';
import ContextMenu from '@/components/ContextMenu/ContextMenu.vue';
import { ContextMenuAction } from '@/components/ContextMenu/types';
import {
  getDefaultContextMenuActions,
  globalContextMenuHeaderItemDefinition,
} from '@/components/ContextMenu/utils';
import EntityChange from '@/components/EntityHistory/EntityChange.vue';
import CommentCount from '@/components/forms/CommentCount/CommentCount.vue';
import { EMPTY_FIELD_SIGN } from '@/components/forms/utils';
import IdentifierLabel from '@/components/IdentifierLabel/IdentifierLabel.vue';
import OverviewTable from '@/components/OverviewTable/OverviewTable.vue';
import OverviewTableHeader from '@/components/OverviewTable/OverviewTableHeader.vue';
import type { SortItem } from '@/components/OverviewTable/types';
import BasePage from '@/components/pages/BasePage/BasePage.vue';
import BasePageTitle from '@/components/pages/BasePage/BasePageTitle.vue';
import { routenames } from '@/router/routenames';
import { useUnitsStore } from '@/stores/unitsStore';
import { alphabeticalStringCompare, localizedEnumCompare } from '@/utils/compareAndSort';
import { translateEntityProperty } from '@/utils/helper.ts';
import { buildOdataQuery } from '@/utils/odata';
import type { I18nKey } from '@/utils/typings';
import { useLoading } from '@/utils/useLoading';
import { useTranslation } from 'i18next-vue';
import { ref, useTemplateRef, watchEffect } from 'vue';
import { useRouter } from 'vue-router';
import UnitOverviewFilter from './components/UnitOverviewFilter.vue';
import { UnitOverviewFilterObject } from './types';
import { getEntityChangeEvent } from '@/components/EntityHistory/entityChange';
import { useRemainingViewportHeight } from '@/utils/useRemainingVueport';
import { usePermissionsStore } from '@/stores/permissionsStore.ts';
import { useTableScrollElement } from '@/utils/vuetifyMenuHelper';

const { t } = useTranslation();
const { hasPermissions } = usePermissionsStore();
const router = useRouter();
const unitsStore = useUnitsStore();
const tableRef = useTemplateRef<HTMLDivElement>('table');
const { height } = useRemainingViewportHeight(tableRef);
const tableScrollElement = useTableScrollElement(tableRef);

const headers = [
  {
    title: ``,
    key: 'name',
    sortRaw(unitA: UnitDto, unitB: UnitDto) {
      const translatedUnitAName = translateEntityProperty('name', unitA);
      const translatedUnitBName = translateEntityProperty('name', unitB);
      if (!translatedUnitAName || !translatedUnitBName) {
        return -1;
      }
      return alphabeticalStringCompare(translatedUnitAName, translatedUnitBName);
    },
  },
  {
    title: t('UnitSymbol'),
    key: 'abbreviation',
  },
  {
    title: t('Description'),
    key: 'description',
    sortRaw(unitA: UnitDto, unitB: UnitDto) {
      const translatedUnitADescription = translateEntityProperty('description', unitA);
      const translatedUnitBDescription = translateEntityProperty('description', unitB);
      if (!translatedUnitADescription || !translatedUnitBDescription) {
        return -1;
      }
      return alphabeticalStringCompare(translatedUnitADescription, translatedUnitBDescription);
    },
    width: '25%',
  },
  {
    title: t('StateOfUse'),
    key: 'state',
    sort: localizedEnumCompare('StateOfUseEnum'),
  },
  {
    title: t('Added'),
    key: 'creationTime',
  },
  {
    title: t('LastModification'),
    key: 'lastModificationTime',
  },
  globalContextMenuHeaderItemDefinition,
] as const;

const filterObject = ref<UnitOverviewFilterObject>({});

const customKeySort = {
  name: alphabeticalStringCompare,
};

const sortBy: SortItem[] = [{ key: 'name', order: 'asc' }];

const units = ref<UnitDto[]>();

const {
  action: loadUnits,
  loading,
  loadingFirstTime,
} = useLoading(async () => {
  const { result } = await api.unitsApi.getUnitList(
    buildOdataQuery({
      search: filterObject.value?.search,
    }),
  );

  if (result?.value) {
    units.value = result.value;
  }
}, true);

function navigateTo(id: string, hash?: string) {
  router.push({
    name: routenames.units.details,
    hash,
    params: { id },
  });
}

const { action: deleteAction } = useActionWithConfirmation({
  confirmedAction: async (unit: UnitDto) => {
    const { success } = await unitsStore.deleteUnit(unit.id);

    if (success) {
      unitsStore.update();
      loadUnits();
    }
  },
  confirmationOptions: {
    title: t('Confirmation'),
    confirmButton: t('Delete'),
    hideClose: false,
  },
});

function getContextMenuActions(item: UnitDto): ContextMenuAction[] {
  const defaultContextMenuActions = getDefaultContextMenuActions(item);
  return [
    {
      ...defaultContextMenuActions.view,
      to: {
        name: routenames.units.details,
        params: { id: item.id },
      },
    },
    {
      ...defaultContextMenuActions.edit,
      to: {
        name: routenames.units.edit,
        params: { id: item.id },
      },
    },
    {
      ...defaultContextMenuActions.delete,
      callback: () => {
        deleteAction({
          param: item,
          confirmationOptions: getDefaultDeleteConfirmationOptions(
            {
              ...(item as EntityInfo),
              typeName: 'Unit_one',
            },
            t,
          ),
        });
      },
    },
  ];
}

watchEffect(loadUnits);
</script>

<template>
  <BasePage :loading="loadingFirstTime">
    <template #title>
      <BasePageTitle :title="$t('Unit_other')" />
    </template>

    <template #actions>
      <PtwAddButton
        v-if="hasPermissions([AppPermissionsEnum.GlobalUnitsAdd])"
        :to="{ name: routenames.units.add }"
      />
    </template>

    <UnitOverviewFilter v-model="filterObject" />
    <OverviewTable
      ref="table"
      class="mt-2"
      :headers="headers"
      :items="units"
      :loading="loading"
      itemLabel="Unit"
      data-testid="DataTable_Unit"
      :customKeySort="customKeySort"
      :sortBy="sortBy"
      :height="height"
      fixedHeader
      @click:row="
        (_event, data) => {
          navigateTo(data.item.id);
        }
      "
    >
      <template #header.name="headerProps">
        <OverviewTableHeader v-bind="headerProps">
          {{ $t('Name') }}
          <span class="font-weight-medium">| {{ $t('ID') }}</span>
        </OverviewTableHeader>
      </template>

      <template #item.name="{ item }">
        <IdentifierLabel :identifier="item.iuid" :label="translateEntityProperty('name', item)" />
        <CommentCount :value="item" @click.stop="navigateTo(item.id, `#${TAB_PREFIX}comments`)" />
      </template>

      <template #item.abbreviation="{ item }">
        {{ item.abbreviation }}
      </template>

      <template #item.description="{ item }">
        <p class="truncated">
          {{ item.description ? translateEntityProperty('description', item) : EMPTY_FIELD_SIGN }}
        </p>
      </template>

      <template #item.state="{ item }">
        {{ $t(`StateOfUseEnum.${item.state}` as I18nKey) }}
        <EntityChange
          :entityChange="getEntityChangeEvent(item, 'editing')"
          :showEmptyFieldSign="false"
        />
      </template>

      <template #item.creationTime="{ item }">
        <EntityChange :entityChange="getEntityChangeEvent(item, 'created')" />
      </template>

      <template #item.lastModificationTime="{ item }">
        <EntityChange :entityChange="getEntityChangeEvent(item, 'modified')" />
      </template>

      <template #item.contextMenu="{ item }">
        <ContextMenu
          :actions="getContextMenuActions(item)"
          :closeOnScrollElements="tableScrollElement"
        />
      </template>
    </OverviewTable>
  </BasePage>
</template>
